import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { SITE } from "@/data/site";
import { PRODUCTS } from "@/data/products";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/bifold-doors", label: "Bifold Doors" },
  { to: "/trade-supply", label: "Trade Supply" },
  { to: "/supply-installation", label: "Supply & Install" },
  { to: "/colour-visualiser", label: "Colours" },
  { to: "/about", label: "Our Factory" },
  { to: "/contact", label: "Contact" },
];

const innerLinks = links.filter((l) => l.to !== "/");

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const location = useLocation();

  const navCls = ({ isActive }) =>
    `whitespace-nowrap text-[13px] xl:text-sm font-semibold tracking-wide transition-colors duration-300 ${
      isActive ? "text-[#215389]" : "text-slate-600 hover:text-[#215389]"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo-link">
            <img src={SITE.logo} alt="Empire Aluminium Windows logo" className="h-9 md:h-11 w-auto" />
            <span className="font-heading font-extrabold text-[#0A121F] leading-none tracking-tight text-left">
              <span className="block text-sm md:text-base uppercase">Empire</span>
              <span className="block text-[10px] md:text-xs uppercase tracking-[0.18em] text-[#215389]">Aluminium Windows</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center flex-1 min-w-0 justify-end gap-2.5 xl:gap-6 pl-6 pr-4" aria-label="Main navigation">
            <div
              className="relative"
              onMouseEnter={() => setProdOpen(true)}
              onMouseLeave={() => setProdOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-[13px] xl:text-sm font-semibold tracking-wide transition-colors duration-300 ${
                  location.pathname.startsWith("/products") ? "text-[#215389]" : "text-slate-600 hover:text-[#215389]"
                }`}
                data-testid="navbar-products-dropdown"
                aria-haspopup="true"
                aria-expanded={prodOpen}
                onClick={() => setProdOpen(true)}
                onKeyDown={(e) => e.key === "Escape" && setProdOpen(false)}
              >
                Products <ChevronDown size={14} className={`transition-transform duration-300 ${prodOpen ? "rotate-180" : ""}`} />
              </button>
              {prodOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[560px]" data-testid="navbar-products-menu">
                  <div className="bg-white border border-slate-200 shadow-2xl rounded-xl p-6 grid grid-cols-2 gap-1">
                    {PRODUCTS.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/products/${p.slug}`}
                        onClick={() => setProdOpen(false)}
                        className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-[#215389] transition-colors duration-200"
                        data-testid={`navbar-product-${p.slug}`}
                      >
                        {p.name}
                        <span className="block text-[11px] text-slate-400 font-normal">{p.category}</span>
                      </Link>
                    ))}
                    <Link
                      to="/products"
                      onClick={() => setProdOpen(false)}
                      className="col-span-2 mt-2 px-3 py-2 text-center text-sm font-bold text-[#215389] bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                      data-testid="navbar-all-products-link"
                    >
                      View all products →
                    </Link>
                  </div>
                </div>
              )}
            </div>
            {innerLinks.filter((l) => l.to !== "/contact").map((l) => (
              <NavLink key={l.to} to={l.to} className={navCls} data-testid={`navbar-link-${l.to.slice(1)}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <a href={SITE.phoneHref} className="hidden 2xl:flex items-center gap-2 text-sm font-bold text-[#0A121F] whitespace-nowrap" data-testid="navbar-phone-link">
              <Phone size={15} className="text-[#29CC9A]" /> {SITE.phone}
            </a>
            <Link
              to="/contact"
              className="px-5 py-2.5 bg-[#29CC9A] text-[#0A121F] text-sm font-bold rounded-full hover:bg-[#22b487] transition-colors duration-300 whitespace-nowrap"
              data-testid="navbar-quote-btn"
            >
              Get a Quote
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-[#0A121F]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 max-h-[80vh] overflow-y-auto" data-testid="navbar-mobile-menu">
          <div className="px-6 py-6 space-y-1">
            <Link to="/" onClick={() => setOpen(false)} className="block py-2.5 font-bold text-[#0A121F]" data-testid="mobile-link-home">Home</Link>
            <Link to="/products" onClick={() => setOpen(false)} className="block py-2.5 font-bold text-[#0A121F]" data-testid="mobile-link-products">Products</Link>
            <div className="pl-4 pb-2 grid grid-cols-1 gap-1">
              {PRODUCTS.map((p) => (
                <Link key={p.slug} to={`/products/${p.slug}`} onClick={() => setOpen(false)} className="block py-1.5 text-sm text-slate-600">
                  {p.name}
                </Link>
              ))}
            </div>
            {innerLinks.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2.5 font-bold text-[#0A121F]" data-testid={`mobile-link-${l.to.slice(1)}`}>
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="block mt-4 px-5 py-3 bg-[#29CC9A] text-[#0A121F] text-center font-bold rounded-full"
              data-testid="mobile-quote-btn"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
